import styled, { css, StyledProps } from 'styled-components'
import { IconCross } from '../../iconography'
import React from 'react'
import { useTranslation } from 'react-i18next'

type Size = 'large' | 'small'

type BannerProps = {
  size?: Size
  primaryText: string
  secondaryText?: string
  className?: string
  id?: string
  learnMore?: boolean // show 'learn more' text or not
  onClick?: () => void
  onClose?: () => void
  styles: null | {
    startColor?: string // single color or background gradient start color
    endColor?: string // background gradient end color
    primaryColor?: string // primary text color
    secondaryColor?: string // secondary text color
  }
}

type StyledTextProps = StyledProps<{ size: Size; color?: string }>
type StyledWrapperProps = StyledProps<{ size: Size; startColor?: string; endColor?: string }>

const wrapperSmall = css`
  padding: 24px 16px 16px;
  text-align: left;
`
const wrapperLarge = css`
  padding: 16px 32px;

  ${props => props.theme.media.mobile`
    ${wrapperSmall};
  `}
`
const Wrapper = styled.div.attrs<StyledWrapperProps>(({ startColor, endColor }) => {
  return {
    style: {
      background: `${
        startColor && endColor ? `linear-gradient(180deg, ${startColor} 0%, ${endColor} 100%)` : startColor || '#ebb320'
      }`,
    },
  }
})<StyledWrapperProps>`
  position: relative;
  overflow: hidden;
  cursor: pointer;
  border-radius: 16px;
  text-align: center;

  ${props => (props.size === 'large' ? wrapperLarge : wrapperSmall)}
`

const primaryTextSmall = css`
  font-size: 14px;
  line-height: 18px;
  text-align: left;
`

const primaryTextLarge = css`
  font-weight: bold;
  font-size: 32px;
  line-height: 40px;
  text-align: center;

  ${props => props.theme.media.tablet`
    font-size: 28px;
    line-height: 40px;
`}

  ${props => props.theme.media.smallTablet`
     font-size: 22px;
     line-height: 32px;
  `}

  ${props => props.theme.media.mobile`
    ${primaryTextSmall};
  `}
`

const PrimaryText = styled.p.attrs<StyledTextProps>(({ color, theme }) => {
  return {
    style: {
      color: color || theme.color.onPrimaryLight,
    },
  }
})<StyledTextProps>`
  font-weight: bold;
  position: relative;
  margin: 0;

  ${props => (props.size === 'large' ? primaryTextLarge : primaryTextSmall)}
`

const secondaryTextSmall = css`
  font-weight: 500;
  font-size: 12px;
  line-height: 16px;
  margin-top: 24px;
  text-align: left;
`

const secondaryTextLarge = css`
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  text-align: center;
  margin-top: 8px;

  ${props => props.theme.media.smallTablet`
     font-size: 14px;
     line-height: 20px;
  `}

  ${props => props.theme.media.mobile`
     ${secondaryTextSmall}
  `}
`

const SecondaryText = styled.p.attrs<StyledTextProps>(({ color, theme }) => {
  return {
    style: {
      color: color || theme.color.onPrimaryLight,
    },
  }
})<StyledTextProps>`
  font-weight: 500;
  margin: 0;
  position: relative;

  ${(props: StyledTextProps) => (props.size === 'large' ? secondaryTextLarge : secondaryTextSmall)}
`

const CloseBtn = styled(IconCross)<StyledTextProps>`
  position: absolute;
  right: 10px;
  top: 10px;
  cursor: pointer;
  transition: 0.3s opacity;
  fill: ${props => props.color || props.theme.color.onPrimaryLight};
  opacity: 0.8;
  width: ${props => (props.size === 'small' ? '14px' : '16px')};
  height: ${props => (props.size === 'small' ? '14px' : '16px')};

  :hover {
    opacity: 1;
  }

  ${props => props.theme.media.mobile`
     right: 12px;
     top: 8px;
     width: 14px;
     height: 14px;
  `}
`

const LearnMore = styled.span<StyledTextProps>`
  text-decoration-line: underline;
  white-space: nowrap;
  opacity: 0.5;
  margin: ${props => (props.size === 'small' ? '4px 0 0' : '0 0 0 4px')};
  display: ${props => (props.size === 'small' ? 'block' : 'inline-block')};

  ${props => props.theme.media.mobile`
     margin: 0 0 0 4px;
     display: block;
  `}
`

export const Banner: React.FC<BannerProps> = ({
  size = 'small',
  primaryText,
  secondaryText,
  className,
  id,
  learnMore = true,
  onClick,
  onClose,
  styles,
}) => {
  const { t } = useTranslation('ui')
  return (
    <Wrapper
      id={id}
      size={size}
      className={className}
      startColor={styles?.startColor}
      endColor={styles?.endColor}
      onClick={onClick}
    >
      {!!onClose && <CloseBtn onClick={onClose} size={size} />}
      <PrimaryText size={size} color={styles?.primaryColor}>
        {primaryText}
      </PrimaryText>
      <SecondaryText size={size} color={styles?.secondaryColor}>
        {secondaryText}
        {learnMore && (
          <LearnMore size={size} color={styles?.secondaryColor}>
            {t('Banner.learnMore')}
          </LearnMore>
        )}
      </SecondaryText>
    </Wrapper>
  )
}
